import { formatBirthdayInfo } from './crmHelpers.ts';
import {
  inferTemplateCategoryFromRules,
  isClientInRuleCategory,
} from './segmentRuleInference.ts';
import type { CrmClient, CrmSegment, CrmSubFilter, SegmentMemberships } from '../types/crm.ts';
import type { Settings } from '../types/settings.ts';

export interface EligibleCrmTemplateOption {
  id: string;
  label: string;
  template: string;
}

/**
 * Returns all templates that a client is strictly qualified to receive based on API data and active context.
 */
export function getEligibleCrmTemplates(
  client: CrmClient,
  activeSegmentId: string,
  activeSubFilter: CrmSubFilter = 'all',
  settings: Settings,
  segmentObj?: CrmSegment,
  memberships?: SegmentMemberships,
  allSegments: CrmSegment[] = []
): { options: EligibleCrmTemplateOption[]; defaultKey: string } {
  const options: EligibleCrmTemplateOption[] = [];

  // Check if client is directly in active segment
  const isInActiveSegment = Boolean(
    activeSegmentId && (
      client.segmentId === activeSegmentId ||
      memberships?.[activeSegmentId]?.includes(client.id)
    )
  );

  // Active segment inferred category (if active segment is present)
  const activeSegmentCategory = segmentObj
    ? inferTemplateCategoryFromRules(segmentObj.rules, segmentObj.name, activeSegmentId)
    : activeSegmentId && allSegments.length > 0
    ? inferTemplateCategoryFromRules(
        allSegments.find((s) => s.id === activeSegmentId)?.rules,
        allSegments.find((s) => s.id === activeSegmentId)?.name,
        activeSegmentId
      )
    : undefined;

  // 1. Birthday Qualification
  const bdayInfo = formatBirthdayInfo(client.birthdayDay, client.birthdayMonth);
  const isBirthdayEligible =
    isClientInRuleCategory(client, 'birthday', allSegments, memberships) ||
    (isInActiveSegment && activeSegmentCategory === 'birthday') ||
    Boolean(bdayInfo && bdayInfo.daysUntil !== null && bdayInfo.daysUntil <= 30);

  if (isBirthdayEligible) {
    options.push({
      id: 'birthday',
      label: '🎂 Cumpleaños',
      template: settings.msgTemplateBirthday,
    });
  }

  // 2. Debt Qualification (Strict: must have positive debt)
  const isDebtEligible = (client.outstandingAmount || 0) > 0.01;
  if (isDebtEligible) {
    options.push({
      id: 'debt',
      label: '💰 Saldo Deudor',
      template: settings.msgTemplateDebtor || settings.templateCampaignDebtor,
    });
  }

  // 3. Top Spender Qualification
  const isTopSpenderEligible =
    isClientInRuleCategory(client, 'top_spender', allSegments, memberships) ||
    (isInActiveSegment && activeSegmentCategory === 'top_spender');

  if (isTopSpenderEligible) {
    options.push({
      id: 'top_spender',
      label: '💎 Mayor Consumo',
      template: settings.msgTemplateTopSpenders,
    });
  }

  // 4. Loyal / VIP Qualification
  const isVipEligible =
    isClientInRuleCategory(client, 'vip', allSegments, memberships) ||
    (isInActiveSegment && activeSegmentCategory === 'vip');

  if (isVipEligible) {
    options.push({
      id: 'vip',
      label: '⭐ Cliente Fiel',
      template: settings.msgTemplateVip || settings.templateCampaignVip,
    });
  }

  // 5. Lapsed / Inactive Qualification (Must not have future appointment)
  const isLapsedEligible =
    !client.nextAppointmentDate &&
    (isClientInRuleCategory(client, 'lapsed', allSegments, memberships) ||
      (isInActiveSegment && activeSegmentCategory === 'lapsed'));

  if (isLapsedEligible) {
    options.push({
      id: 'lapsed',
      label: '💤 Inactivo / Ausente',
      template: settings.msgTemplateLapsed || settings.templateCampaignLapsed,
    });
  }

  // 6. New Clients Qualification
  const isNewEligible =
    isClientInRuleCategory(client, 'new', allSegments, memberships) ||
    isClientInRuleCategory(client, 'first_visit', allSegments, memberships) ||
    (isInActiveSegment && (activeSegmentCategory === 'new' || activeSegmentCategory === 'first_visit'));

  if (isNewEligible) {
    options.push({
      id: 'new',
      label: '👋 Nuevo Cliente',
      template: settings.msgTemplateNewClients,
    });
  }

  // 7. Custom Segment Override (if active segment has explicit custom template)
  if (activeSegmentId && settings.crmSegmentTemplates?.[activeSegmentId]) {
    options.push({
      id: 'custom_segment',
      label: `✨ ${segmentObj?.name || 'Segmento Activo'}`,
      template: settings.crmSegmentTemplates[activeSegmentId],
    });
  }

  // 8. General Template (Always available fallback)
  options.push({
    id: 'default',
    label: '📄 General',
    template: settings.templateCrmDefault,
  });

  // Calculate Smart Default Key based on intent priority
  let defaultKey = 'default';

  if (activeSubFilter && activeSubFilter !== 'all') {
    const subFilterMap: Record<string, string> = {
      birthday: 'birthday',
      debt: 'debt',
      top_spender: 'top_spender',
      vip: 'vip',
      lapsed: 'lapsed',
      new: 'new',
    };
    const targetKey = subFilterMap[activeSubFilter];
    if (targetKey && options.some((o) => o.id === targetKey)) {
      defaultKey = targetKey;
      return { options, defaultKey };
    }
  }

  const inferred = activeSegmentCategory || 'default';

  if (isDebtEligible && inferred !== 'birthday') {
    defaultKey = 'debt';
  } else if (activeSegmentId && settings.crmSegmentTemplates?.[activeSegmentId]) {
    defaultKey = 'custom_segment';
  } else if (activeSegmentId && inferred !== 'default' && options.some((o) => o.id === inferred)) {
    defaultKey = inferred;
  } else {
    defaultKey = options[0]?.id || 'default';
  }

  return { options, defaultKey };
}
